import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
  SimpleSnackBar,
} from '@angular/material/snack-bar';
import {
  NotificationOptions,
  NotificationState,
  IChatNotificationService,
} from '../interfaces/chat-ui-service.interface';
import { formatErrorForUser } from '../../api/fetch-error.utils';

/**
 * Service responsible for managing chat notifications.
 * Handles all notification display, state management, and lifecycle.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatNotificationService implements IChatNotificationService, OnDestroy {
  // ===== Notification State Management =====
  private readonly notificationSubject = new BehaviorSubject<NotificationState | null>(null);
  private readonly activeNotifications = new Set<MatSnackBarRef<SimpleSnackBar>>();

  // ===== Public Observables =====
  public readonly currentNotification$ = this.notificationSubject.asObservable();

  constructor(private snackBar: MatSnackBar) {}

  // ===== Core Notification Methods =====

  /**
   * Shows a success notification
   */
  showSuccess(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    const config = this.createConfig(options, ['chat-notification', 'success']);
    return this.show(message, 'success', config, options);
  }

  /**
   * Shows an error notification
   */
  showError(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    const config = this.createConfig(options, ['chat-notification', 'error']);
    config.duration = options?.duration || 5000; // Longer duration for errors
    return this.show(message, 'error', config, options);
  }

  /**
   * Shows a warning notification
   */
  showWarning(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    const config = this.createConfig(options, ['chat-notification', 'warning']);
    return this.show(message, 'warning', config, options);
  }

  /**
   * Shows an info notification
   */
  showInfo(message: string, options?: NotificationOptions): MatSnackBarRef<SimpleSnackBar> {
    const config = this.createConfig(options, ['chat-notification', 'info']);
    return this.show(message, 'info', config, options);
  }

  /**
   * Dismisses all active notifications
   */
  dismissAllNotifications(): void {
    this.snackBar.dismiss();
    this.activeNotifications.forEach((ref) => ref.dismiss());
    this.activeNotifications.clear();
  }

  // ===== Pre-built Notifications =====

  /**
   * Notifies when file size exceeds limit
   */
  notifyFileTooLarge(maxSize: number): void {
    this.showError(`File size exceeds the maximum limit of ${maxSize}MB`);
  }

  /**
   * Notifies when file type is not supported
   */
  notifyUnsupportedFileType(fileType: string): void {
    this.showError(`File type "${fileType}" is not supported`);
  }

  /**
   * Notifies successful file upload
   */
  notifyUploadSuccess(fileName: string): void {
    this.showSuccess(`File "${fileName}" uploaded successfully`);
  }

  /**
   * Notifies upload error
   */
  notifyUploadError(error: string): void {
    this.showError(`Upload failed: ${error}`);
  }

  /**
   * Notifies successful copy to clipboard
   */
  notifyCopySuccess(): void {
    this.showSuccess('Message copied to clipboard', { duration: 2000 });
  }

  /**
   * Notifies successful PDF export
   */
  notifyPdfExportSuccess(): void {
    this.showSuccess('PDF downloaded successfully', { duration: 2000 });
  }

  /**
   * Notifies PDF export error
   */
  notifyPdfExportError(error: string): void {
    this.showError(`PDF export failed: ${error}`, { duration: 3000 });
  }

  /**
   * Notifies connection error
   */
  notifyConnectionError(): void {
    this.showError('Connection error. Please check your internet connection and try again.', {
      duration: 5000,
      action: 'Retry',
    });
  }

  /**
   * Notifies rate limit exceeded
   */
  notifyRateLimitExceeded(): void {
    this.showWarning('Rate limit exceeded. Please wait before sending another message.', {
      duration: 5000,
    });
  }

  // ===== Specific Notification Methods =====

  /**
   * Shows a notification for file upload error with details
   */
  showFileUploadError(fileName: string, error: string): MatSnackBarRef<SimpleSnackBar> {
    return this.showError(`Failed to upload "${fileName}": ${error}`);
  }

  /**
   * Shows a notification for message copy error
   */
  showMessageCopyError(): MatSnackBarRef<SimpleSnackBar> {
    return this.showError('Failed to copy message to clipboard');
  }

  /**
   * Shows a notification for API errors
   */
  showApiError(error: any): MatSnackBarRef<SimpleSnackBar> {
    // Use formatErrorForUser for FetchError objects to get user-friendly messages
    let message: string;
    if (error && typeof error === 'object' && 'status' in error) {
      message = formatErrorForUser(error);
    } else {
      message = this.extractErrorMessage(error);
    }

    return this.showError(message, { duration: 5000 });
  }

  /**
   * Shows a notification for authentication errors
   */
  showAuthError(): MatSnackBarRef<SimpleSnackBar> {
    return this.showError('Authentication failed. Please log in again.', {
      duration: 5000,
      action: 'Login',
    });
  }

  /**
   * Shows a notification for unsupported file types with details
   */
  showUnsupportedFileError(
    fileName: string,
    supportedTypes: string[],
  ): MatSnackBarRef<SimpleSnackBar> {
    const types = supportedTypes.join(', ');
    return this.showError(`File "${fileName}" is not supported. Supported types: ${types}`);
  }

  /**
   * Shows a notification for file size errors
   */
  showFileSizeError(fileName: string, maxSize: string): MatSnackBarRef<SimpleSnackBar> {
    return this.showError(`File "${fileName}" is too large. Maximum size: ${maxSize}`);
  }

  /**
   * Dismisses a specific notification
   */
  dismiss(notificationRef: MatSnackBarRef<SimpleSnackBar>): void {
    notificationRef.dismiss();
    this.activeNotifications.delete(notificationRef);
  }

  /**
   * Gets the current notification state
   */
  getCurrentNotification(): NotificationState | null {
    return this.notificationSubject.value;
  }

  // ===== Lifecycle =====

  ngOnDestroy(): void {
    // Dismiss all notifications
    this.dismissAllNotifications();

    // Complete subjects
    this.notificationSubject.complete();
  }

  // ===== Private Helper Methods =====

  /**
   * Creates MatSnackBar configuration
   */
  private createConfig(
    options?: NotificationOptions,
    additionalClasses: string[] = [],
  ): MatSnackBarConfig {
    const panelClass = Array.isArray(options?.panelClass)
      ? [...additionalClasses, ...options.panelClass]
      : [...additionalClasses, ...(options?.panelClass ? [options.panelClass] : [])];

    return {
      duration: options?.duration || 4000,
      panelClass,
      verticalPosition: options?.verticalPosition || 'bottom',
      horizontalPosition: options?.horizontalPosition || 'center',
    };
  }

  /**
   * Shows a notification and manages its lifecycle
   */
  private show(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info',
    config: MatSnackBarConfig,
    options?: NotificationOptions,
  ): MatSnackBarRef<SimpleSnackBar> {
    const action = options?.action || 'Close';
    const ref = this.snackBar.open(message, action, config);

    // Track active notification
    this.activeNotifications.add(ref);

    // Update notification state
    this.notificationSubject.next({
      message,
      type,
      timestamp: Date.now(),
      options,
    });

    // Clean up when dismissed
    ref.afterDismissed().subscribe(() => {
      this.activeNotifications.delete(ref);
    });

    return ref;
  }

  /**
   * Extracts error message from various error types
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.error && typeof error.error === 'string') {
      return error.error;
    }

    return 'An unexpected error occurred. Please try again.';
  }
}
